import React, { useState, useEffect, useRef, useContext } from "react";
import { pkceUserProfile } from "@/types/auth/pkceUserProfile";
import { AuthStatus } from "@/types/auth/AuthStatus";
import { CloudOSDeploymentEnvironment } from "@/types/shared/CloudOSDeploymentEnvironment";
import { PkceAuthEnvironment } from "@/types/auth/PkceAuthEnvironment";
import isTokenValid from "@/utilities/auth/isTokenValid";
import {
  AuthChannelMessage,
  AuthSession,
  getTokenSecondsLeft,
  hasSdkTokenCache,
  isAuthRedirectCallback,
  isUsableSession,
  openAuthChannel,
  requestSessionFromPeers
} from "@/utilities/auth/crossTabAuth";
import { clearSessionCache } from "@/utilities/auth/sessionCache";
import getConfig from "next/config";
import cookie from "js-cookie";
import { useRouter } from "next/router";
import axios from "axios";

function getPkceAuthEnvironment(environment: CloudOSDeploymentEnvironment): PkceAuthEnvironment {
  const envToAuthEnv: {
    [key in CloudOSDeploymentEnvironment]: PkceAuthEnvironment;
  } = {
    development: "dev",
    staging: "stg",
    production: "prod",
    "": "dev"
  };

  if (typeof envToAuthEnv[environment] === "undefined") return "dev";

  return envToAuthEnv[environment];
}

const AuthContext = React.createContext<{
  userProfile: pkceUserProfile | null;
  signIn: () => Promise<null>;
  signOut: () => Promise<null>;
  authStatus: AuthStatus;
  accessToken: string;
  vpnConnect: number;
  checkVPNConnection: any;
  apiEnv: string;
}>({
  userProfile: null,
  signIn: () => new Promise((resolve) => resolve(null)),
  signOut: () => new Promise((resolve) => resolve(null)),
  authStatus: "pending",
  accessToken: "",
  vpnConnect: 0,
  checkVPNConnection: () => {},
  apiEnv: "dev"
});

export default AuthContext;

const { publicRuntimeConfig } = getConfig() || {};

// Tabs without their own SDK cache (opened via handoff) hold no refresh token. They wait for the
// tab that does to refresh and broadcast, and only fall back to the SDK on the last 30s refresh
// tick before the token expires.
const SDK_FALLBACK_SECONDS_LEFT = 35;

export function AuthProvider({
  clientId,
  environment,
  children
}: {
  clientId: string;
  environment: CloudOSDeploymentEnvironment;
  children: React.ReactNode;
}) {
  const effectHasBeenCalled = useRef<boolean>(false);
  const [authStatus, setAuthStatus] = useState<AuthStatus>("pending");
  const [userProfile, setUserProfile] = useState<pkceUserProfile | null>(null);
  const [vpnConnect, setVpnConnect] = useState<number>(0);
  const [accessToken, setAccessToken] = useState("");
  const [apiEnv, setApiEnv] = useState("dev");
  const router = useRouter();
  const { pathname, asPath } = useRouter();
  let vpnMode = false;

  // Refs mirror the auth state for the BroadcastChannel handler and refresh timer, which are
  // created once and would otherwise read stale closure values.
  const registerPromiseRef = useRef<Promise<void> | null>(null);
  const authChannelRef = useRef<BroadcastChannel | null>(null);
  const accessTokenRef = useRef("");
  const userProfileRef = useRef<pkceUserProfile | null>(null);
  const authStatusRef = useRef<AuthStatus>("pending");

  useEffect(() => {
    accessTokenRef.current = accessToken;
    userProfileRef.current = userProfile;
    authStatusRef.current = authStatus;
  }, [accessToken, userProfile, authStatus]);

  useEffect(() => {
    if (!clientId || !environment) return;
    /**
     * Despite my best efforts, the `register` method is being called multiple
     * times, which is returning console errors. This is due to React strict
     * mode, and it is a problem that can likely be solved at a later time.
     */
    if (effectHasBeenCalled.current === true) return;
    const protocol = typeof window !== "undefined" ? window?.location?.protocol : "";

    const canLoadPkceAuth = protocol === "https:";

    // if (!canLoadPkceAuth) {
    //   setAuthStatus("https-required");
    //   return;
    // }

    const redirectUri = window.location.origin;

    effectHasBeenCalled.current = true;

    var tmpEnv = "dev";
    if (environment == "production") {
      tmpEnv = "";
      setApiEnv("prd");
    } else if (environment == "staging") {
      tmpEnv = "-stg";
      setApiEnv("stg");
    } else if (environment == "development") {
      tmpEnv = "-dev";
      setApiEnv("dev");
    }

    async function registerAuth() {
      await window.cfp.providers.auth.register({
        clientId: clientId,
        redirectUri: redirectUri,
        environment: getPkceAuthEnvironment(environment),
        logoutRedirectUri: redirectUri,
        useRefreshTokens: true,
        cacheLocation: "sessionstorage" // Default is memory which is for SPAs
      });
    }

    async function fetchUserProfileAndToken(): Promise<AuthSession> {
      // const userProfile: pkceUserProfile = await window.cfp.providers.auth.getUserProfile();

      const token: string = await window.cfp.providers.auth.getAccessToken();
      accessTokenRef.current = token;
      setAccessToken(token);
      setupRefreshTimer();

      const profile = await axios.get(`https://api.userprofile${tmpEnv}.autodesk.com/userinfo`, {
        headers: {
          Authorization: token
          // "Content-Type": "application/json"
        }
      });
      const userProfile: pkceUserProfile = {
        emailId: profile.data?.email,
        userId: profile.data?.eidm_guid,
        firstName: profile.data?.given_name,
        lastName: profile.data?.family_name,
        emailVerified: profile.data?.email_verified,
        "2FaEnabled": profile.data?.is_2fa_enabled,
        countryCode: profile.data?.country_code,
        language: profile.data?.language,
        optin: true,
        lastModified: profile.data?.updated_at,
        profileImages: profile.data?.thumbnails,
        ldapInfo: { ldapEnabled: profile.data?.ldap_enabled, domainName: profile.data?.ldap_domain },
        socialUserInfoList: profile.data?.social_userinfo_list,
        createdDate: profile.data?.created_date,
        lastLoginDate: profile.data?.last_login_date,
        eidmGuid: profile.data?.eidm_guid,
        contact: profile.data?.address,
        userName: profile.data?.preferred_username
      };
      const session: AuthSession = { accessToken: token, userProfile };
      applySession(session);
      return session;
    }

    (async () => {
      try {
        if (window.self !== window.top) return; // Return early if in an iframe

        // Tokens used to be mirrored into JS-readable cookies that nothing read; clear any left over.
        cookie.remove("accessToken");
        cookie.remove("userId");

        const authChannel = openAuthChannel();
        if (authChannel) {
          authChannel.onmessage = handleAuthChannelMessage;
          authChannelRef.current = authChannel;
        }

        const registerPromise = registerAuth();
        registerPromise.catch(() => {}); // Awaited below; avoids an unhandled rejection on the handoff path.
        registerPromiseRef.current = registerPromise;

        // A tab opened from the app starts with an empty SDK cache (sessionStorage is per tab), so
        // borrow the session of an already signed-in tab instead of redoing silent auth.
        const peerSession = !hasSdkTokenCache() && !isAuthRedirectCallback() ? await requestSessionFromPeers() : null;
        if (peerSession) {
          applySession(peerSession);
          setupRefreshTimer();
          return;
        }

        await registerPromise;

        const session = await fetchUserProfileAndToken();
        broadcastSession(session);

        // await ensurePublisherProfile(userProfile, token);
      } catch (_e) {
        console.error(_e);
        //signIn();
        setAuthStatus("logged-out");
      }
    })();
  }, [clientId, environment]);

  const checkVPNConnection = (env: string) => {
    setVpnConnect(0);
    const apiKey = process.env.NEXT_PUBLIC_JOB_LIST_COMMERCIAL_PROD_KEY || "";
    // const access_token = "asdfWE34wegsdfg2345234wegsdfg343434f2323";
    let url = `https://api.pps-${env}.autodesk.com/health`;
    axios
      .get(url, {
        headers: {
          // "x-api-key": apiKey,
          // "access-token": access_token,
          "Content-Type": "application/json"
        }
      })
      .then(function (response) {
        setVpnConnect(1);
        vpnMode = true;
      })
      .catch((error) => {
        console.log("error", error);
        if (error.response) {
          setVpnConnect(1);
          vpnMode = true;
        } else {
          setVpnConnect(-1);
          vpnMode = false;
        }
      });
  };

  useEffect(() => {
    if (!environment) return;

    let env = "dev";
    if (environment == "production") {
      env = "prd";
    } else if (environment == "staging") {
      env = "stg";
    } else if (environment == "development") {
      env = "dev";
    }
    // let interval: any = null;
    // interval = setInterval(() => {
    if (!vpnMode) {
      checkVPNConnection(env);
    }
    // }, 2000);

    // return () => {
    //     if (interval) {
    //         clearInterval(interval);
    //     }
    // };
  }, [environment]);

  useEffect(() => {
    if (authStatus === "pending" || vpnConnect == 0) {
      return;
    }

    if (vpnConnect == -1) {
      router.push("/login");
    }

    if (!accessToken) {
      router.push("/login");
    } else {
      const redirectUrl = sessionStorage.getItem("redirectUrl");
      if (redirectUrl) {
        sessionStorage.removeItem("redirectUrl");
        // Only redirect if there's a stored redirectUrl (after login flow)
        if (redirectUrl.includes("login")) {
          router.push("/");
        } else {
          router.push(redirectUrl);
        }
      }
      // If no redirectUrl, user is already on the correct page - don't modify URL
    }
  }, [accessToken, authStatus, vpnConnect]);

  async function getAccessToken() {
    try {
      await registerPromiseRef.current;
      const accessToken = await window.cfp.providers.auth.getAccessToken();

      return accessToken;
    } catch (error) {
      console.log(error);

      await window.cfp.providers.auth.login();
    }
  }

  function applySession(session: AuthSession) {
    accessTokenRef.current = session.accessToken;
    userProfileRef.current = session.userProfile;
    authStatusRef.current = "logged-in";
    setAccessToken(session.accessToken);
    setUserProfile(session.userProfile);
    setAuthStatus("logged-in");
    window.userEmail = session.userProfile.emailId;
  }

  function postAuthMessage(message: AuthChannelMessage) {
    authChannelRef.current?.postMessage(message);
  }

  function broadcastSession(session: AuthSession) {
    postAuthMessage({ type: "session-updated", session });
  }

  function getCurrentSession(): AuthSession | null {
    const userProfile = userProfileRef.current;
    if (authStatusRef.current !== "logged-in" || !userProfile) return null;
    return { accessToken: accessTokenRef.current, userProfile };
  }

  /** Takes a token refreshed by another tab when it belongs to the same user and outlives ours. */
  function adoptPeerSession(session: AuthSession) {
    const current = getCurrentSession();
    if (!current || !isUsableSession(session) || session.userProfile.userId !== current.userProfile.userId) return;
    if (getTokenSecondsLeft(session.accessToken) <= getTokenSecondsLeft(current.accessToken)) return;
    accessTokenRef.current = session.accessToken;
    setAccessToken(session.accessToken);
  }

  /**
   * Another tab signed out. Run the SDK logout here too so this tab's own token cache is cleared
   * and it goes through the identity logout redirect rather than auto-signing back in.
   */
  async function logoutFromPeer() {
    if (authStatusRef.current === "logged-out") return;
    try {
      await registerPromiseRef.current;
      await window.cfp.providers.auth.logout({ logoutRedirectUri: window.location.origin });
    } catch (error) {
      console.error(error);
      accessTokenRef.current = "";
      setAccessToken("");
      setUserProfile(null);
      setAuthStatus("logged-out");
    }
  }

  function handleAuthChannelMessage(event: MessageEvent<AuthChannelMessage>) {
    const message = event.data;
    switch (message?.type) {
      case "session-request": {
        const session = getCurrentSession();
        if (isUsableSession(session)) {
          postAuthMessage({ type: "session-response", requestId: message.requestId, session });
        }
        break;
      }
      case "session-updated":
        adoptPeerSession(message.session);
        break;
      case "logout":
        logoutFromPeer();
        break;
    }
  }

  async function refreshAccessToken() {
    const currentToken = accessTokenRef.current;
    if (isTokenValid(currentToken)) return;

    let token: string | undefined;
    if (hasSdkTokenCache()) {
      token = await getAccessToken();
    } else {
      const peerSession = await requestSessionFromPeers();
      if (peerSession && peerSession.userProfile.userId === userProfileRef.current?.userId) {
        token = peerSession.accessToken;
      } else if (getTokenSecondsLeft(currentToken) < SDK_FALLBACK_SECONDS_LEFT) {
        token = await getAccessToken();
      }
    }

    if (token && token !== currentToken) {
      accessTokenRef.current = token;
      setAccessToken(token);
      const userProfile = userProfileRef.current;
      if (userProfile) broadcastSession({ accessToken: token, userProfile });
    }
  }

  function setupRefreshTimer() {
    setTimeout(async function () {
      try {
        await refreshAccessToken();
      } catch (error) {
        console.error(error);
      }
      setupRefreshTimer();
    }, 30000);
  }

  function signIn() {
    return window.cfp.providers.auth
      .login()
      .then(() => setAuthStatus("logged-in"))
      .catch(() => setAuthStatus("logged-out"));
  }

  function signOut() {
    postAuthMessage({ type: "logout" });
    clearSessionCache();

    setAuthStatus("logged-out");

    const envSuffix = getPkceAuthEnvironment(environment);
    return window.cfp.providers.auth
      .logout({
        logoutRedirectUri: window.location.origin
      })
      .catch(() => setAuthStatus("logged-out"));
  }

  return (
    <AuthContext.Provider
      value={{
        signIn,
        signOut,
        userProfile,
        authStatus,
        accessToken,
        apiEnv,
        vpnConnect,
        checkVPNConnection
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
